import { useMemo } from "react";
import { WORKSHOPS, type Workshop, type WorkshopStatus } from "@/content/workshops";
import { useProgress, resolveStatus } from "@/stores/useProgress";
import { useDecisions } from "@/stores/useDecisions";
import { useActions } from "@/stores/useActions";
import { useAssumptions } from "@/stores/useAssumptions";
import { useTranscripts } from "@/stores/useTranscripts";
import { useCaptures } from "@/stores/useCaptures";
import { useAssetLocker } from "@/stores/useAssetLocker";
import { useFindings } from "@/stores/useFindings";
import { useOpportunities } from "@/stores/useOpportunities";
import { useRecommendations } from "@/stores/useRecommendations";
import { useFacilitationNotes } from "@/stores/useFacilitationNotes";

/**
 * PROG-ROLLUP — a memoized, read-across-the-edges view of the whole
 * program: per-workshop record/artefact counts + completion, plus program
 * totals. It never stores derived state (master plan §8: "read across
 * stores at the edges, never store derived state") — every number is
 * recomputed from live store snapshots.
 *
 * Hydration is the whole risk (plan2 Risk #6): Mission Control only
 * hydrates useCaptures + useAssetLocker at boot, so reading the register
 * stores before they hydrate would show zeros then jump. {@link
 * hydrateAllRegisters} + the `ready` guard close that — a consumer shows a
 * loading state until every store it reads has hydrated.
 *
 * Scope: aggregates every register — the R1-shipped ones plus the Track A
 * net-new registers (findings / opportunities / recommendations /
 * facilitation notes), wired in at the three extension points Track B
 * reserved.
 */

export type RollupCounts = {
  decisions: number;
  actions: number;
  assumptions: number;
  transcripts: number;
  captures: number;
  uploads: number;
  findings: number;
  opportunities: number;
  recommendations: number;
  facilitationNotes: number;
};

export type WorkshopRollup = {
  workshopId: Workshop["id"];
  title: string;
  /** Effective status (override ?? static default), same source as the
   *  navigator badge. */
  status: WorkshopStatus;
  counts: RollupCounts;
  /** Sum of all record/artefact counts for the workshop. */
  total: number;
};

/** A program-wide open action, flattened across workshops for the
 *  "what's next" surface. */
export type UpcomingAction = {
  id: string;
  title: string;
  owner: string;
  /** `YYYY-MM-DD`, legacy free-text, or "" when undated. */
  due: string;
  workshopId: Workshop["id"];
  workshopTitle: string;
};

/** A program-wide decision, flattened across workshops for the
 *  "decisions made" surface. */
export type RecentDecision = {
  id: string;
  title: string;
  owner: string;
  /** proposed | agreed | parked (kept as string — the panel maps it to a dot). */
  status: string;
  createdAt: number;
  workshopId: Workshop["id"];
  workshopTitle: string;
};

/** How many open actions / recent decisions the program rollup surfaces. */
const MAX_ROLLUP_LIST = 4;

export type ProgramRollup = {
  /** True once every store the rollup reads has hydrated — gate UI on this
   *  to avoid the zeros-then-jump flash. */
  ready: boolean;
  byWorkshop: WorkshopRollup[];
  totals: RollupCounts & { total: number };
  workshopsComplete: number;
  workshopsTotal: number;
  /** Open actions across the whole program (dated first, soonest due first),
   *  capped — the Client US1 "what's next" surface. */
  upcomingActions: UpcomingAction[];
  /** Latest decisions across the whole program (newest first), capped — the
   *  Client US1 "decisions made" surface. */
  recentDecisions: RecentDecision[];
};

/**
 * Idempotently hydrate every store the rollup reads. Each store's
 * `hydrate()` guards on its own `isHydrated`, so calling this repeatedly
 * (e.g. on every panel mount) is cheap. Call it before reading the rollup.
 */
export async function hydrateAllRegisters(): Promise<void> {
  await Promise.all([
    useProgress.getState().hydrate(),
    useDecisions.getState().hydrate(),
    useActions.getState().hydrate(),
    useAssumptions.getState().hydrate(),
    useTranscripts.getState().hydrate(),
    useCaptures.getState().hydrate(),
    useAssetLocker.getState().hydrate(),
    useFindings.getState().hydrate(),
    useOpportunities.getState().hydrate(),
    useRecommendations.getState().hydrate(),
    useFacilitationNotes.getState().hydrate(),
  ]);
}

const ZERO_TOTALS: ProgramRollup["totals"] = {
  decisions: 0,
  actions: 0,
  assumptions: 0,
  transcripts: 0,
  captures: 0,
  uploads: 0,
  findings: 0,
  opportunities: 0,
  recommendations: 0,
  facilitationNotes: 0,
  total: 0,
};

/**
 * Subscribe to every register + progress and compute the program rollup.
 * Recomputes only when one of the underlying `entriesByWorkshop` maps or
 * the progress override map changes.
 */
export function useProgramRollup(): ProgramRollup {
  const decisions = useDecisions((s) => s.entriesByWorkshop);
  const actions = useActions((s) => s.entriesByWorkshop);
  const assumptions = useAssumptions((s) => s.entriesByWorkshop);
  const transcripts = useTranscripts((s) => s.entriesByWorkshop);
  const captures = useCaptures((s) => s.entriesByWorkshop);
  const uploads = useAssetLocker((s) => s.entriesByWorkshop);
  const findings = useFindings((s) => s.entriesByWorkshop);
  const opportunities = useOpportunities((s) => s.entriesByWorkshop);
  const recommendations = useRecommendations((s) => s.entriesByWorkshop);
  const facilitationNotes = useFacilitationNotes((s) => s.entriesByWorkshop);
  const workshopStatus = useProgress((s) => s.workshopStatus);

  const dHydrated = useDecisions((s) => s.isHydrated);
  const aHydrated = useActions((s) => s.isHydrated);
  const asHydrated = useAssumptions((s) => s.isHydrated);
  const tHydrated = useTranscripts((s) => s.isHydrated);
  const cHydrated = useCaptures((s) => s.isHydrated);
  const uHydrated = useAssetLocker((s) => s.isHydrated);
  const fHydrated = useFindings((s) => s.isHydrated);
  const oHydrated = useOpportunities((s) => s.isHydrated);
  const rHydrated = useRecommendations((s) => s.isHydrated);
  const nHydrated = useFacilitationNotes((s) => s.isHydrated);
  const pHydrated = useProgress((s) => s.isHydrated);

  const ready =
    dHydrated &&
    aHydrated &&
    asHydrated &&
    tHydrated &&
    cHydrated &&
    uHydrated &&
    fHydrated &&
    oHydrated &&
    rHydrated &&
    nHydrated &&
    pHydrated;

  return useMemo(() => {
    const byWorkshop: WorkshopRollup[] = WORKSHOPS.map((w) => {
      const counts: RollupCounts = {
        decisions: decisions[w.id]?.length ?? 0,
        actions: actions[w.id]?.length ?? 0,
        assumptions: assumptions[w.id]?.length ?? 0,
        transcripts: transcripts[w.id]?.length ?? 0,
        captures: captures[w.id]?.length ?? 0,
        uploads: uploads[w.id]?.length ?? 0,
        findings: findings[w.id]?.length ?? 0,
        opportunities: opportunities[w.id]?.length ?? 0,
        recommendations: recommendations[w.id]?.length ?? 0,
        facilitationNotes: facilitationNotes[w.id]?.length ?? 0,
      };
      const total =
        counts.decisions +
        counts.actions +
        counts.assumptions +
        counts.transcripts +
        counts.captures +
        counts.uploads +
        counts.findings +
        counts.opportunities +
        counts.recommendations +
        counts.facilitationNotes;
      return {
        workshopId: w.id,
        title: w.title,
        status: resolveStatus(workshopStatus, w.id, w.status),
        counts,
        total,
      };
    });

    const totals = byWorkshop.reduce(
      (acc, w) => {
        acc.decisions += w.counts.decisions;
        acc.actions += w.counts.actions;
        acc.assumptions += w.counts.assumptions;
        acc.transcripts += w.counts.transcripts;
        acc.captures += w.counts.captures;
        acc.uploads += w.counts.uploads;
        acc.findings += w.counts.findings;
        acc.opportunities += w.counts.opportunities;
        acc.recommendations += w.counts.recommendations;
        acc.facilitationNotes += w.counts.facilitationNotes;
        acc.total += w.total;
        return acc;
      },
      { ...ZERO_TOTALS },
    );

    // "What's next" — every open action across the program, dated soonest
    // first, undated last (then newest), capped. Items already exist + are
    // editable in the workshop log; this just surfaces them in one place.
    const upcomingActions: UpcomingAction[] = WORKSHOPS.flatMap((w) =>
      (actions[w.id] ?? [])
        .filter((a) => a.status !== "done")
        .map((a) => ({
          id: a.id,
          title: a.title,
          owner: a.owner,
          due: a.due,
          workshopId: w.id,
          workshopTitle: w.title,
        })),
    )
      .sort((x, y) => {
        const dx = x.due.trim();
        const dy = y.due.trim();
        if (dx && dy) return dx < dy ? -1 : dx > dy ? 1 : 0;
        if (dx) return -1; // dated before undated
        if (dy) return 1;
        return 0;
      })
      .slice(0, MAX_ROLLUP_LIST);

    // "Decisions made" — latest decisions across the program, newest first.
    const recentDecisions: RecentDecision[] = WORKSHOPS.flatMap((w) =>
      (decisions[w.id] ?? []).map((d) => ({
        id: d.id,
        title: d.title,
        owner: d.owner,
        status: d.status,
        createdAt: d.createdAt,
        workshopId: w.id,
        workshopTitle: w.title,
      })),
    )
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, MAX_ROLLUP_LIST);

    return {
      ready,
      byWorkshop,
      totals,
      workshopsComplete: byWorkshop.filter((w) => w.status === "done").length,
      workshopsTotal: byWorkshop.length,
      upcomingActions,
      recentDecisions,
    };
  }, [
    decisions,
    actions,
    assumptions,
    transcripts,
    captures,
    uploads,
    findings,
    opportunities,
    recommendations,
    facilitationNotes,
    workshopStatus,
    ready,
  ]);
}
